<template>
  <div v-if="edit && newProduct">
    <!-- Add Product Button - All Screen Sizes -->
    <button
      v-if="compactTrigger"
      type="button"
      class="add-product-compact"
      @click="openSearchModal"
    >
      <i class="mdi mdi-plus-circle-outline"></i>
      <span class="add-label">{{ $t("products_box.add_product") }}</span>
      <span class="add-hint">{{ $t("products_box.add_product_hint") }}</span>
      <i class="mdi mdi-magnify add-search"></i>
    </button>
    <div v-else class="add-product-trigger">
      <label class="block text-2xl font-semibold text-gray-600 mb-2"
        >Add Product</label
      >
      <button
        @click="openSearchModal"
        class="w-full flex items-center gap-2 px-5 py-4 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors"
      >
        <i class="mdi mdi-magnify text-2xl text-blue-600"></i>
        <span class="text-xl text-gray-500"
          >Tap to search and add product...</span
        >
      </button>
    </div>

    <!-- Search Modal - Responsive (Full screen mobile, centered panel desktop) -->
    <div
      v-if="showSearchModal"
      ref="mobileModal"
      class="search-modal-overlay"
      :class="{ 'modal-hidden': searchModalHidden }"
    >
      <div class="search-modal-content">
        <!-- SEARCH STEP -->
        <template v-if="modalStep === 'search'">
          <div class="flex-1 flex flex-col h-full">
            <!-- Modal Header -->
            <div class="modal-header">
              <button
                @click="closeSearchModal"
                class="w-12 h-12 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300"
              >
                <i class="mdi mdi-close text-2xl text-gray-700"></i>
              </button>
              <div class="flex-1 flex flex-col gap-2">
                <input
                  ref="modalSearchInput"
                  class="flex-1 px-5 py-3 border border-gray-300 rounded-lg text-lg"
                  type="text"
                  :placeholder="
                    searchMode === 'typesense'
                      ? 'Fast search...'
                      : 'Search NKR Code...'
                  "
                  @input="onModalSearchInput"
                  v-model="modalSearchQuery"
                />
                <div class="search-mode-toggle">
                  <button
                    type="button"
                    :class="['mode-btn', { active: searchMode === 'api' }]"
                    @click="switchSearchMode('api')"
                  >
                    API (accurate)
                  </button>
                  <button
                    type="button"
                    :class="[
                      'mode-btn typesense',
                      { active: searchMode === 'typesense' }
                    ]"
                    @click="switchSearchMode('typesense')"
                  >
                    <i class="mdi mdi-flash"></i> Typesense (fast)
                  </button>
                </div>
                <label class="in-stock-toggle">
                  <input type="checkbox" v-model="inStockOnly" />
                  <span>In stock only</span>
                </label>
              </div>
            </div>

            <!-- Search Results -->
            <div class="flex-1 overflow-y-auto p-3">
              <!-- Quick Add Custom Option -->
              <div
                v-if="modalSearchQuery && modalSearchQuery.length > 0"
                class="mb-3 p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-center justify-between"
              >
                <span class="text-base md:text-lg text-orange-700">
                  Can't find it? Add "<strong>{{ modalSearchQuery }}</strong
                  >" as custom
                </span>
                <button
                  @click="selectCustomItemFromModal()"
                  class="bg-orange-500 hover:bg-orange-600 text-white text-base md:text-lg px-4 py-2 rounded-lg transition-colors"
                >
                  + Custom
                </button>
              </div>

              <!-- TYPESENSE RESULTS -->
              <template v-if="searchMode === 'typesense'">
                <div v-if="typesenseLoading" class="text-center py-12">
                  <BaseMiniSpinner />
                  <div class="text-gray-500 text-base mt-2">
                    Searching Typesense...
                  </div>
                </div>
                <template v-else-if="filteredTypesenseResults.length > 0">
                  <div class="search-results-grid">
                    <SearchResultItem
                      v-for="item in filteredTypesenseResults"
                      :key="item.objectID"
                      :item="item"
                      :priceLevel="priceLevel"
                      :departmentCode="departmentCode"
                      :transformedPriceLevel="transformedPriceLevel"
                      :selectLoading="
                        loadingTypesenseProduct ===
                          ((item.nkrCode && item.nkrCode[0]) || item.objectID)
                      "
                      @select="selectTypesenseItem(item)"
                      @quick-add="$emit('addProduct', $event)"
                      @view="viewProduct($event)"
                      @alternatives="searchAlternatives($event)"
                    />
                  </div>
                  <div v-if="typesenseHasMore" class="load-more-wrap">
                    <button
                      type="button"
                      class="load-more-btn"
                      :disabled="typesenseLoadingMore"
                      @click="loadMoreTypesense"
                    >
                      <BaseMiniSpinner v-if="typesenseLoadingMore" />
                      <template v-else>
                        <i class="mdi mdi-chevron-down"></i> Load more
                      </template>
                    </button>
                  </div>
                </template>
                <template
                  v-else-if="
                    modalSearchQuery &&
                      modalSearchQuery.length > 0 &&
                      !typesenseLoading
                  "
                >
                  <div class="text-center py-12">
                    <i
                      class="mdi mdi-alert-circle-outline text-5xl text-gray-300 mb-3 block"
                    ></i>
                    <div class="text-gray-500 text-base">
                      No products found in Typesense
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="text-center py-16 text-gray-400">
                    <i class="mdi mdi-flash text-5xl mb-3 block"></i>
                    <div class="text-base md:text-lg">
                      Type to search with Typesense
                    </div>
                  </div>
                </template>
              </template>

              <!-- API RESULTS (existing) -->
              <template
                v-else-if="filteredApiOptions && filteredApiOptions.length > 0"
              >
                <div class="search-results-grid">
                  <div
                    v-for="item in filteredApiOptions"
                    :key="item.nkrCode"
                    class="search-result-item"
                  >
                    <!-- Added badge -->
                    <div
                      v-if="quickAddedItems[item.nkrCode]"
                      class="flex items-center gap-1 px-3 py-1 bg-green-50 border-b border-green-200 text-green-700 text-sm font-semibold"
                    >
                      <i class="mdi mdi-check-circle text-base"></i> Added to
                      order
                    </div>

                    <!-- Product Header (clickable to select) -->
                    <div
                      class="flex items-center gap-3 cursor-pointer p-3 hover:bg-blue-50 rounded-lg transition-colors"
                      @click="selectItemFromModal(item)"
                    >
                      <div class="result-image">
                        <img
                          v-if="item.image"
                          :src="item.image"
                          class="w-full h-full object-contain"
                        />
                        <i
                          v-else
                          class="mdi mdi-image-off text-2xl text-gray-300"
                        ></i>
                      </div>
                      <div class="flex-1 min-w-0">
                        <div
                          class="text-base md:text-lg font-bold text-gray-900 truncate"
                        >
                          {{ item.nkrCode }}
                        </div>
                        <div
                          class="text-base md:text-lg text-gray-600 truncate"
                        >
                          {{ item.customTitle || item.originalTitle }}
                        </div>
                        <div class="text-xs text-gray-400 mt-0.5">
                          <span class="font-semibold text-gray-600">{{
                            item.brand
                          }}</span>
                          • {{ item.pack }}
                          <span v-if="item.stock" class="ml-1 text-green-600"
                            >({{ item.stock }} in stock)</span
                          >
                        </div>
                      </div>
                      <i
                        class="mdi mdi-chevron-right text-xl text-gray-400"
                      ></i>
                    </div>

                    <!-- Stock & Price Summary -->
                    <div
                      v-if="item.pack !== 'KIT' && item.pricesAndStock"
                      class="px-3 pb-2"
                    >
                      <StockSummaryTags
                        :pricesAndStock="item.pricesAndStock"
                        :currentDepartmentCode="departmentCode"
                        :priceLevel="transformedPriceLevel"
                        :showPrices="true"
                      />
                    </div>

                    <!-- Action buttons row -->
                    <div class="flex border-t border-gray-100">
                      <!-- Quick Add Button -->
                      <button
                        v-if="item.pack !== 'KIT'"
                        @click.stop="quickAddApiItem(item)"
                        class="flex-1 text-base md:text-lg font-semibold py-2.5 transition-colors flex items-center justify-center gap-1 border-r border-gray-100"
                        :class="
                          quickAddedItems[item.nkrCode]
                            ? 'bg-green-50 text-green-700 hover:bg-green-100'
                            : 'text-green-700 hover:bg-green-50'
                        "
                      >
                        <i
                          :class="
                            quickAddedItems[item.nkrCode]
                              ? 'mdi mdi-check'
                              : 'mdi mdi-plus'
                          "
                        ></i>
                        {{ quickAddedItems[item.nkrCode] ? "Added" : "Add" }}
                      </button>

                      <!-- View Product Button -->
                      <button
                        @click.stop="viewProduct(item.nkrCode)"
                        class="flex-1 text-base md:text-lg text-gray-600 hover:text-gray-800 hover:bg-gray-50 py-2.5 transition-colors flex items-center justify-center gap-1"
                      >
                        <i class="mdi mdi-eye-outline"></i>
                        View
                      </button>

                      <!-- Search Alternatives Button -->
                      <button
                        v-if="item.unificado"
                        @click.stop="searchAlternatives(item.unificado)"
                        class="flex-1 text-base md:text-lg text-blue-600 hover:text-blue-800 hover:bg-blue-50 py-2.5 border-l border-gray-100 transition-colors flex items-center justify-center gap-1"
                      >
                        <i class="mdi mdi-magnify"></i>
                        Alternatives
                      </button>
                    </div>
                  </div>
                </div>
                <div v-if="apiHasMore" class="load-more-wrap">
                  <button
                    type="button"
                    class="load-more-btn"
                    :disabled="apiLoadingMore"
                    @click="loadMoreApi"
                  >
                    <BaseMiniSpinner v-if="apiLoadingMore" />
                    <template v-else>
                      <i class="mdi mdi-chevron-down"></i> Load more
                    </template>
                  </button>
                </div>
              </template>
              <template
                v-else-if="
                  searchMode === 'api' && options && options.length === 0
                "
              >
                <div class="text-center py-12">
                  <i
                    class="mdi mdi-alert-circle-outline text-5xl text-gray-300 mb-3 block"
                  ></i>
                  <div class="text-gray-500 text-base mb-4">
                    No products found
                  </div>
                  <button
                    @click="selectCustomItemFromModal()"
                    class="bg-red-500 hover:bg-red-600 text-white py-2.5 px-6 rounded-lg transition-colors"
                  >
                    + Add as Custom Product
                  </button>
                </div>
              </template>
              <template v-else-if="searchMode === 'api' && !options">
                <div class="text-center py-16 text-gray-400">
                  <i class="mdi mdi-magnify text-5xl mb-3 block"></i>
                  <div class="text-base md:text-lg">
                    Enter 4+ characters to search
                  </div>
                </div>
              </template>
            </div>
          </div>
        </template>

        <!-- EDIT STEP (after product selection) -->
        <template v-else-if="modalStep === 'edit'">
          <div class="flex-1 flex flex-col h-full">
            <!-- Header -->
            <div class="modal-header">
              <button
                @click="modalStep = 'search'"
                class="w-10 h-10 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300"
              >
                <i class="mdi mdi-arrow-left text-xl text-gray-700"></i>
              </button>
              <span class="font-semibold text-gray-800 text-lg"
                >Confirm Product</span
              >
              <!-- {{ newProductLines }} -->
              <button
                @click="closeSearchModal"
                class="ml-auto w-10 h-10 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300"
              >
                <i class="mdi mdi-close text-xl text-gray-700"></i>
              </button>
            </div>

            <!-- Content -->
            <div class="flex-1 overflow-y-auto p-4">
              <!-- Selected Product Card -->
              <div
                class="selected-product-card"
                :class="newProduct.custom ? 'custom' : 'normal'"
              >
                <div class="flex items-center gap-4">
                  <div class="selected-product-image">
                    <img
                      v-if="newProduct.image"
                      :src="newProduct.image"
                      class="w-full h-full object-contain"
                    />
                    <i
                      v-else-if="newProduct.custom"
                      class="mdi mdi-pencil-plus text-3xl text-orange-500"
                    ></i>
                    <i
                      v-else
                      class="mdi mdi-image-off text-3xl text-gray-300"
                    ></i>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 flex-wrap">
                      <span class="text-base font-bold text-gray-900">
                        {{ newProduct.nkrCode }}
                      </span>
                      <span
                        v-if="newProduct.unificado"
                        class="text-sm font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-300"
                      >
                        {{ newProduct.unificado }}
                      </span>
                      <span
                        v-if="newProduct.custom"
                        class="text-xs bg-orange-500 text-white px-2 py-0.5 rounded"
                      >
                        Custom
                      </span>
                    </div>
                    <div
                      v-if="newProduct.description"
                      class="text-base md:text-lg text-gray-600 mt-1"
                    >
                      {{ newProduct.description }}
                    </div>
                    <div
                      v-if="newProduct.brand"
                      class="text-base md:text-lg text-gray-500 mt-0.5"
                    >
                      {{ newProduct.brand }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Warehouse Selection (if multiple options) -->
              <div v-if="validProductLines" class="mt-4">
                <div class="flex items-center justify-between mb-2">
                  <label
                    class="text-base md:text-lg font-semibold text-gray-700"
                  >
                    Select Warehouse ({{ validProductLines.length }} options)
                  </label>
                  <button
                    @click="warehouseExpanded = !warehouseExpanded"
                    class="text-base md:text-lg text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <span>{{
                      warehouseExpanded ? "Collapse" : "Show All"
                    }}</span>
                    <i
                      :class="
                        warehouseExpanded
                          ? 'mdi mdi-chevron-up'
                          : 'mdi mdi-chevron-down'
                      "
                    ></i>
                  </button>
                </div>

                <!-- Selected/First warehouse always visible -->
                <div
                  v-if="!warehouseExpanded && selectedWarehouseLine"
                  class="warehouse-option"
                  :class="{ selected: !newProduct.isTransfer }"
                  @click="warehouseExpanded = true"
                  style="cursor: pointer;"
                >
                  <div class="flex-1">
                    <div class="text-base md:text-lg font-semibold">
                      {{
                        selectedWarehouseLine.warehouse ||
                          selectedWarehouseLine.departamentoCodigo
                      }}
                      • {{ selectedWarehouseLine.departamentoCodigo }} •
                      {{ selectedWarehouseLine.line }}
                    </div>
                    <div class="text-base md:text-lg text-gray-500">
                      {{
                        selectedWarehouseLine[transformedPriceLevel]
                          | currencyFormatted(true)
                      }}
                      • Stock: {{ selectedWarehouseLine.stock }}
                    </div>
                  </div>
                  <i
                    v-if="!newProduct.isTransfer"
                    class="mdi mdi-check-circle text-xl text-green-600"
                  ></i>
                </div>

                <!-- Expanded list -->
                <!-- Expanded list -->
                <div v-if="warehouseExpanded" class="space-y-4">
                  <!-- Valid warehouses -->
                  <div class="warehouse-list">
                    <div
                      v-for="(line, index) in validProductLines"
                      :key="index"
                      class="warehouse-option"
                      :class="{
                        selected:
                          line.productId === newProduct.productId &&
                          line.warehouseId === newProduct.warehouseId &&
                          line.departamentoCodigo === newProduct.departmentCode
                      }"
                      @click="
                        selectProductLine(line);
                        warehouseExpanded = false;
                      "
                    >
                      <div class="flex-1">
                        <div class="text-base md:text-lg font-semibold">
                          {{ line.warehouse || line.departamentoCodigo }} •
                          {{ line.departamentoCodigo }} •
                          {{ line.line }}
                        </div>
                        <div class="text-base md:text-lg text-gray-500">
                          {{
                            line[transformedPriceLevel]
                              | currencyFormatted(true)
                          }}
                          • Stock: {{ line.stock }}
                          <span
                            v-if="
                              line.warehouseDisabled === '1' ||
                                line.warehouseDisabled === 1
                            "
                            class="ml-2 text-yellow-600 text-xs font-medium"
                          >
                            (Not Enabled)
                          </span>
                        </div>
                      </div>
                      <i
                        v-if="
                          line.productId === newProduct.productId &&
                            line.warehouseId === newProduct.warehouseId &&
                            line.departamentoCodigo ===
                              newProduct.departmentCode
                        "
                        class="mdi mdi-check-circle text-xl text-green-600"
                      ></i>
                    </div>
                  </div>

                  <!-- Invalid warehouses (disabled or no stock) -->
                  <div
                    v-if="invalidProductLines && invalidProductLines.length > 0"
                  >
                    <div class="text-xs font-semibold text-gray-500 mb-2">
                      <i class="mdi mdi-lock-outline mr-1"></i>
                      Unavailable ({{ invalidProductLines.length }})
                    </div>
                    <div class="warehouse-list opacity-60">
                      <div
                        v-for="(line, index) in invalidProductLines"
                        :key="'invalid-' + index"
                        class="warehouse-option disabled-warehouse"
                      >
                        <div class="flex-1">
                          <div class="text-base md:text-lg font-semibold">
                            {{ line.warehouse }} •
                            {{ line.departamentoCodigo }} •
                            {{ line.line }}
                          </div>
                          <div class="text-base md:text-lg text-gray-500">
                            {{
                              line[transformedPriceLevel]
                                | currencyFormatted(true)
                            }}
                            • Stock: {{ line.stock }}
                            <span class="ml-2 text-red-600 text-xs font-medium">
                              (Cannot Sell)
                            </span>
                          </div>
                        </div>
                        <i class="mdi mdi-lock-outline text-gray-400"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Transfer Option - Show when stock exists in other departments -->
              <div
                v-if="
                  transferableProductLines &&
                    transferableProductLines.length > 0
                "
                class="mt-4"
              >
                <div
                  class="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-3"
                >
                  <div class="flex items-center gap-2 text-blue-700 mb-1">
                    <i class="mdi mdi-truck-fast text-lg"></i>
                    <span class="text-base font-semibold"
                      >Transfer available</span
                    >
                  </div>
                  <p class="text-sm text-blue-600">
                    This product has stock in other warehouses. Select one below
                    to add as a TRANSFER line.
                  </p>
                </div>

                <label
                  class="text-base md:text-lg font-semibold text-gray-700 mb-2 block"
                >
                  <i class="mdi mdi-swap-horizontal mr-1 text-blue-600"></i>
                  Transfer from ({{ transferableProductLines.length }} options)
                </label>

                <div class="warehouse-list">
                  <div
                    v-for="(line, index) in transferableProductLines"
                    :key="'transfer-' + index"
                    class="warehouse-option transfer-option"
                    :class="{
                      selected:
                        newProduct.isTransfer &&
                        line.productId === newProduct.productId &&
                        line.warehouseId === newProduct.warehouseId &&
                        line.line === newProduct.line
                    }"
                    @click="selectProductLineAsTransfer(line)"
                  >
                    <div class="flex-1">
                      <div
                        class="text-base md:text-lg font-semibold flex items-center gap-2"
                      >
                        <span
                          class="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded"
                          >TRANSFER</span
                        >
                        {{ line.warehouse || line.departamentoCodigo }} •
                        {{ line.departamentoCodigo }} •
                        {{ line.line }}
                      </div>
                      <div class="text-base md:text-lg text-gray-500">
                        {{
                          line[transformedPriceLevel] | currencyFormatted(true)
                        }}
                        • Stock:
                        <span class="text-green-600 font-medium">{{
                          line.stock
                        }}</span>
                      </div>
                    </div>
                    <i
                      v-if="
                        newProduct.isTransfer &&
                          line.productId === newProduct.productId &&
                          line.warehouseId === newProduct.warehouseId
                      "
                      class="mdi mdi-check-circle text-xl text-blue-600"
                    ></i>
                    <i
                      v-else
                      class="mdi mdi-truck-fast text-xl text-blue-400"
                    ></i>
                  </div>
                </div>
              </div>

              <!-- Form Fields -->
              <div class="edit-form-grid">
                <div v-if="newProduct.pack !== 'KIT'" class="form-field">
                  <label>Line</label>
                  <input
                    type="text"
                    v-model="newProduct.line"
                    :disabled="!editable"
                    placeholder="line"
                  />
                </div>
                <div class="form-field">
                  <label>Brand</label>
                  <input
                    type="text"
                    :disabled="!editable"
                    v-model="newProduct.brand"
                    placeholder="brand"
                  />
                </div>
                <div class="form-field full-width">
                  <label>Description</label>
                  <input
                    type="text"
                    v-model="newProduct.description"
                    placeholder="description"
                  />
                </div>
                <div v-if="priceAndQuantity" class="form-field">
                  <label>
                    Price
                    <span
                      v-if="selectedWarehouseMinPrice"
                      class="text-xs text-gray-500 font-normal ml-1"
                      >(min:
                      {{
                        selectedWarehouseMinPrice | currencyFormatted(true)
                      }})</span
                    >
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    v-model="newProduct.price"
                    placeholder="0.00"
                    :disabled="newProduct.pack == 'KIT'"
                  />
                </div>
                <div v-if="priceAndQuantity" class="form-field">
                  <label>Quantity</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    v-model="newProduct.quantity"
                    placeholder="1"
                  />
                </div>
              </div>
            </div>

            <!-- Action Buttons (sticky footer) -->
            <div class="modal-footer">
              <button
                v-if="valid && newProduct.nkrCode !== '' && !existAlready"
                class="action-btn"
                :class="newProduct.isTransfer ? 'transfer' : 'primary'"
                @click="saveChangesFromModal()"
              >
                <i
                  :class="
                    newProduct.isTransfer
                      ? 'mdi mdi-truck-fast mr-1'
                      : 'mdi mdi-plus mr-1'
                  "
                ></i>
                {{ newProduct.isTransfer ? "Add Transfer" : "Add Product" }}
              </button>
              <button
                v-else-if="
                  newProduct.custom &&
                    newProduct.nkrCode !== '' &&
                    newProduct.price &&
                    newProduct.quantity
                "
                class="action-btn custom"
                @click="addCustomProductFromModal()"
              >
                <i class="mdi mdi-plus mr-1"></i> Add Custom Product
              </button>
              <div v-else-if="existAlready" class="action-btn warning">
                Already in order
              </div>
              <button
                v-else-if="newProduct.pack == 'KIT'"
                class="action-btn kit"
                @click="addKitFromModal()"
              >
                <i class="mdi mdi-package-variant mr-1"></i> Add Kit
              </button>
              <div v-else-if="newProduct.custom" class="action-btn disabled">
                Fill price &amp; qty to add
              </div>
              <button class="cancel-btn" @click="closeSearchModal">
                Cancel
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>

    <NewProductLineKitData
      v-if="kitInformation"
      :kitData="kitInformation"
    ></NewProductLineKitData>

    <!-- Product Modal -->
    <BaseProductModal
      v-if="viewProductCode"
      :code="viewProductCode"
      v-model="showProductModal"
      @input="onProductModalClose"
    />
  </div>
</template>
<script>
import { debounce } from "lodash";
import { getProducts, loadKitItems } from "@/api/store/ProductsService.js";
import NewProductLineKitData from "./NewProductLineKitData.vue";
import StockSummaryTags from "./StockSummaryTags.vue";
import SearchResultItem from "./SearchResultItem.vue";
import { searchProducts as searchTypesense } from "@/utils/productSearch";
import { mapGetters } from "vuex";
import { selectBestLine } from "@/utils/product-line.js";
let upFirst = word => word[0].toUpperCase() + word.toLowerCase().slice(1);

let camelize = text => {
  let words = text.split(/[-_]/g); // ok one simple regexp.
  return words[0].toLowerCase() + words.slice(1).map(upFirst);
};
export default {
  name: "NewProductLine",
  data() {
    return {
      newProduct: {
        productId: null,
        nkrCode: "",
        line: "",
        description: "",
        brand: "",
        price: null,
        pack: "",
        kitCode: "",
        quantity: 1,
        warehouseId: null,
        warehouseCode: null,
        departmentCode: null,
        custom: false,
        isTransfer: false,
        sourceDepartmentCode: null,
        targetDepartmentCode: null,
        unificado: null
      },
      kitInformation: null,
      newProductLines: [],
      validProductLines: [],
      invalidProductLines: [],
      transferableProductLines: [],
      options: null,
      selected: false,
      currentApiCallId: 0,
      showSearchModal: false,
      modalSearchQuery: "",
      modalStep: "search", // "search" or "edit"
      warehouseExpanded: false,
      modalOriginalParent: null,
      modalOriginalNextSibling: null,
      showProductModal: false,
      viewProductCode: null,
      searchModalHidden: false,
      searchMode: "typesense", // "api" or "typesense"
      typesenseResults: [],
      typesenseLoading: false,
      typesenseLoadingMore: false,
      typesensePage: 1,
      typesenseFound: 0,
      apiPage: 1,
      apiTotalPages: 0,
      apiLoadingMore: false,
      loadingTypesenseProduct: null,
      quickAddedItems: {},
      inStockOnly: false
    };
  },
  props: {
    edit: {
      type: Boolean,
      required: true
    },
    priceLevel: {
      type: String,
      required: false,
      default: "online"
    },
    products: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    },
    priceAndQuantity: {
      type: Boolean,
      required: false,
      default() {
        return true;
      }
    },
    departmentCode: {
      type: String,
      required: true
    },
    // Boton de una linea (ProductsBox); el resto de usos mantiene el bloque grande
    compactTrigger: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters("general", ["getActiveStore"]),
    editable() {
      if (!this.newProduct.productId) {
        return true;
      }

      if (this.newProduct.custom) {
        return true;
      }

      return false;
    },
    existAlready() {
      return this.products.some(
        product =>
          product.productId === this.newProduct.productId &&
          product.warehouseId === this.newProduct.warehouseId &&
          product.sourceDepartmentCode === this.newProduct.sourceDepartmentCode
      );
    },
    getAdminSettings() {
      return this.$store.getters["general/getAdminSettings"];
    },
    currency() {
      const currencyObj = this.getAdminSettings.get("currency");
      return currencyObj?.value || "$";
    },
    selectedWarehouseLine() {
      // Find the warehouse line that matches the current selection
      if (!this.validProductLines || this.validProductLines.length === 0) {
        return null;
      }
      const selected = this.validProductLines.find(
        line =>
          line.productId === this.newProduct.productId &&
          line.warehouseId === this.newProduct.warehouseId &&
          line.departamentoCodigo === this.newProduct.departmentCode
      );
      if (selected) return selected;
      const selectableLines = this.validProductLines.filter(
        line => line.warehouse !== "FBA" && line.line !== "OB"
      );
      if (selectableLines.length === 0) return this.validProductLines[0];
      return selectBestLine(selectableLines, this.transformedPriceLevel);
    },
    userCanSeePrecio4() {
      return this.$store.getters["auth/userHasPermission"]("price_precio4");
    },
    userCanSeePrecio5() {
      return this.$store.getters["auth/userHasPermission"]("price_precio5");
    },
    userCanSeePrecio6() {
      return this.$store.getters["auth/userHasPermission"]("price_precio6");
    },
    priceColumns() {
      let columns = 7;
      if (this.userCanSeePrecio4) {
        columns++;
      }
      if (this.userCanSeePrecio5) {
        columns++;
      }
      if (this.userCanSeePrecio6) {
        columns++;
      }
      return `grid-cols-${columns}`;
    },

    transformedPriceLevel() {
      return camelize(this.priceLevel);
    },
    typesenseHasMore() {
      return this.typesenseResults.length < this.typesenseFound;
    },
    apiHasMore() {
      return !!this.options && this.apiPage < this.apiTotalPages;
    },
    filteredTypesenseResults() {
      if (!this.inStockOnly) return this.typesenseResults;
      return this.typesenseResults.filter(item => {
        const total =
          (item.price_and_stock && item.price_and_stock.totalStock) || 0;
        return total > 0;
      });
    },
    filteredApiOptions() {
      if (!this.options) return this.options;
      if (!this.inStockOnly) return this.options;
      return this.options.filter(item => {
        if (item.stock && Number(item.stock) > 0) return true;
        const lines = item.pricesAndStock || [];
        return lines.some(line => Number(line.stock) > 0);
      });
    },
    transformedMinPriceLevel() {
      const base = this.transformedPriceLevel;
      return base.charAt(0).toUpperCase() + base.slice(1);
    },
    selectedWarehouseMinPrice() {
      if (!this.selectedWarehouseLine) return null;
      const key = this.transformedPriceLevel + "MinPrice";
      return this.selectedWarehouseLine[key] || null;
    },
    valid() {
      if (this.newProduct.productId == null) {
        return false;
      }
      if (this.newProduct.quantity == 0) {
        this.$toast.open({
          message: "The quantity cant't be 0",
          type: "error",
          position: "top-right"
        });
        return false;
      }
      return true;
    }
  },
  methods: {
    openSearchModal() {
      this.showSearchModal = true;
      this.modalSearchQuery = "";
      this.modalStep = "search";
      this.options = null;
      this.typesenseResults = [];
      this.loadingTypesenseProduct = null;
      // Lock body scroll on iOS and add class to override parent overflow
      document.body.classList.add("modal-open");
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.height = "100%";
      this.$nextTick(() => {
        // Move modal to body for iOS Safari
        if (
          this.$refs.mobileModal &&
          this.$refs.mobileModal.parentNode !== document.body
        ) {
          this.modalOriginalParent = this.$refs.mobileModal.parentNode;
          this.modalOriginalNextSibling = this.$refs.mobileModal.nextSibling;
          document.body.appendChild(this.$refs.mobileModal);
        }
        if (this.$refs.modalSearchInput) {
          this.$refs.modalSearchInput.focus();
        }
      });
    },
    closeSearchModal() {
      // Move modal back to original position before hiding (for Vue's v-if to work)
      if (this.$refs.mobileModal && this.modalOriginalParent) {
        if (this.modalOriginalNextSibling) {
          this.modalOriginalParent.insertBefore(
            this.$refs.mobileModal,
            this.modalOriginalNextSibling
          );
        } else {
          this.modalOriginalParent.appendChild(this.$refs.mobileModal);
        }
      }
      // Restore body scroll and remove class
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.height = "";
      // Reset state
      this.showSearchModal = false;
      this.modalSearchQuery = "";
      this.modalStep = "search";
      this.options = null;
      this.warehouseExpanded = false;
      this.quickAddedItems = {};
      this.quickAddingItem = null;
    },
    onModalSearchInput() {
      if (this.searchMode === "typesense") {
        this.searchTypesense(this.modalSearchQuery);
      } else {
        this.searchPart(this.modalSearchQuery);
      }
    },
    switchSearchMode(mode) {
      this.searchMode = mode;
      this.options = null;
      this.typesenseResults = [];
      if (this.modalSearchQuery) {
        this.onModalSearchInput();
      }
    },
    searchTypesense: debounce(async function(query) {
      if (!query || query.length < 2) {
        this.typesenseResults = [];
        this.typesensePage = 1;
        this.typesenseFound = 0;
        return;
      }
      this.typesenseLoading = true;
      try {
        const result = await this.fetchTypesensePage(query, 1);
        // Query changed while loading: discard
        if (query !== this.modalSearchQuery) return;
        this.typesenseResults = result.hits;
        this.typesensePage = 1;
        this.typesenseFound = result.found;
      } catch (e) {
        console.error("Typesense search error", e);
        this.typesenseResults = [];
        this.typesenseFound = 0;
      } finally {
        this.typesenseLoading = false;
      }
    }, 250),
    async loadMoreTypesense() {
      const query = this.modalSearchQuery;
      if (!this.typesenseHasMore) return;
      this.typesenseLoadingMore = true;
      try {
        const page = this.typesensePage + 1;
        const result = await this.fetchTypesensePage(query, page);
        // Query changed while loading: discard
        if (query !== this.modalSearchQuery) return;
        this.typesenseResults = this.typesenseResults.concat(result.hits);
        this.typesensePage = page;
        this.typesenseFound = result.found;
      } catch (e) {
        console.error("Typesense load more error", e);
      } finally {
        this.typesenseLoadingMore = false;
      }
    },
    // Misma búsqueda que part-search (utils/productSearch.js), sin facets:
    // hits con nkrCode, título resaltado y precio/stock por tienda.
    fetchTypesensePage(query, page) {
      return searchTypesense({
        collection: this.getActiveStore.code,
        query,
        page,
        hitsPerPage: 40,
        facets: false
      });
    },
    async selectTypesenseItem(typesenseItem) {
      const nkrCode =
        (typesenseItem.nkrCode && typesenseItem.nkrCode[0]) ||
        typesenseItem.objectID;
      this.loadingTypesenseProduct = nkrCode;
      try {
        const filters = {
          storeEnabled: "enabled",
          page: 1,
          limitPerPage: "10",
          orderCol: "nkr_code",
          orderDir: "asc",
          search: nkrCode,
          requestPrice: true,
          priceLevel: "online",
          departmentCode: this.departmentCode
        };
        const res = await getProducts(filters);
        const match = res.products.find(p => p.nkrCode === nkrCode);
        if (match) {
          this.selectItemFromModal(match);
        } else if (res.products.length > 0) {
          this.selectItemFromModal(res.products[0]);
        } else {
          this.$toast.open({
            message: "Product not found in API. Try adding as custom.",
            type: "warning",
            position: "top-right"
          });
        }
      } catch (e) {
        console.error("Failed to load product from API", e);
        this.$toast.open({
          message: "Failed to load product details",
          type: "error",
          position: "top-right"
        });
      } finally {
        this.loadingTypesenseProduct = null;
      }
    },
    clearSelection() {
      this.newProduct.nkrCode = "";
      this.newProduct.description = "";
      this.newProduct.brand = "";
      this.newProduct.productId = null;
      this.newProduct.line = null;
      this.newProduct.warehouseId = null;
      this.newProduct.warehouseCode = null;
      this.newProduct.departmentCode = null;
      this.newProductLines = [];
      this.validProductLines = [];
      this.invalidProductLines = [];
      this.transferableProductLines = [];
      this.kitInformation = null;
    },
    clearForm() {
      this.newProduct = {
        nkrCode: "",
        description: "",
        brand: "",
        price: 0,
        quantity: 1,
        kitCode: "",
        warehouseId: null,
        warehouseCode: null,
        custom: false,
        productId: null,
        line: null,
        isTransfer: false,
        sourceDepartmentCode: null,
        targetDepartmentCode: null
      };
      this.newProductLines = [];
      this.validProductLines = [];
      this.invalidProductLines = [];
      this.transferableProductLines = [];
      this.kitInformation = null;
      this.options = null;
      this.modalSearchQuery = "";
    },
    selectItemFromModal(item) {
      this.selectItem(item);
      this.modalStep = "edit";
    },
    selectCustomItemFromModal() {
      this.newProduct.nkrCode = this.modalSearchQuery;
      this.newProduct.productId = `custom-${this.modalSearchQuery}`;
      this.newProduct.description = "";
      this.newProduct.brand = "";
      this.newProduct.price = null;
      this.newProduct.quantity = 1;
      this.newProduct.line = "";
      this.newProduct.coreNkrCode = null;
      this.newProduct.custom = true;
      this.newProduct.departmentCode = this.departmentCode;
      this.newProduct.warehouseId = null;
      this.newProduct.warehouseCode = null;
      this.newProductLines = [];
      this.modalStep = "edit";
    },
    saveChangesFromModal() {
      if (this.valid) {
        // Use the same logic as desktop saveChanges
        this.newProduct.pricesAndStock = this.newProductLines;
        this.$emit("addProduct", this.newProduct);
        this.clearForm();
        this.closeSearchModal();
      }
    },
    addKitFromModal() {
      this.addKit();
      this.closeSearchModal();
    },
    addCustomProductFromModal() {
      if (this.newProduct.nkrCode != "" && this.newProduct.price !== 0) {
        this.newProduct.productId = `custom-${this.newProduct.nkrCode}`;
        this.$emit("addProduct", this.newProduct);
        this.clearForm();
        this.closeSearchModal();
      }
    },
    searchAlternatives(unificado) {
      this.modalSearchQuery = unificado;
      this.onModalSearchInput();
    },
    quickAddApiItem(item) {
      this._quickEmitProduct(item, item.nkrCode);
    },
    _quickEmitProduct(item, trackKey) {
      if (item.pack === "KIT") return;
      const lines = item.pricesAndStock || [];
      const validLines = lines.filter(
        line =>
          line.canSell === true &&
          line.departamentoCodigo === this.departmentCode
      );
      const selectableLines = validLines.filter(
        line => line.warehouse !== "FBA" && line.line !== "OB"
      );
      if (selectableLines.length === 0) {
        this.$toast.open({
          message: `No available stock in your department for ${trackKey}`,
          type: "warning",
          position: "top-right"
        });
        return;
      }
      const priceKey = this.transformedPriceLevel;
      const best = selectBestLine(selectableLines, priceKey);
      const product = {
        productId: best.productId,
        nkrCode: item.nkrCode,
        line: best.line,
        description: item.customTitle || item.originalTitle,
        brand: item.brand,
        image: item.image,
        pack: item.pack,
        price: best[priceKey],
        quantity: 1,
        warehouseId: best.warehouseId,
        warehouseCode: best.warehouse,
        departmentCode: best.departamentoCodigo,
        unificado: item.unificado || null,
        coreNkrCode: best.coreNkrCode || null,
        custom: false,
        isTransfer: false,
        pricesAndStock: lines
      };
      this.$emit("addProduct", product);
      this.$set(this.quickAddedItems, trackKey, true);
      this.$toast.open({
        message: `${item.nkrCode} added to order`,
        type: "success",
        position: "top-right"
      });
    },
    viewProduct(nkrCode) {
      this.viewProductCode = nkrCode;
      this.showProductModal = true;
      // Temporarily hide the search modal so product modal appears on top
      this.searchModalHidden = true;
    },
    onProductModalClose(value) {
      if (!value) {
        this.showProductModal = false;
        this.viewProductCode = null;
        // Restore search modal visibility
        this.searchModalHidden = false;
      }
    },
    async selectItem(item) {
      this.newProduct.nkrCode = item.nkrCode;
      this.newProduct.description = item.customTitle || item.originalTitle;
      this.newProduct.brand = item.brand;
      this.newProduct.image = item.image;
      this.newProduct.unificado = item.unificado || null;
      this.newProduct.pack = item.pack;
      this.newProduct.core = item.core;
      this.newProduct.coreNkrCode = null;
      this.newProduct.custom = false;

      if (item.pack == "KIT") {
        this.kitInformation = await loadKitItems(this.newProduct.nkrCode);
        this.newProduct.price = this.kitInformation.price;
        this.newProduct.brand = this.kitInformation.brand;
      } else {
        this.kitInformation = null;
        this.newProductLines = item.pricesAndStock || [];

        // Valid = canSell is true AND same department
        this.validProductLines = this.newProductLines.filter(
          line =>
            line.canSell === true &&
            line.departamentoCodigo === this.departmentCode
        );

        // Invalid = everything else
        this.invalidProductLines = this.newProductLines.filter(
          line =>
            line.canSell !== true ||
            line.departamentoCodigo !== this.departmentCode
        );

        // Transferable = different department, canSell is true, and has stock > 0
        this.transferableProductLines = this.newProductLines.filter(
          line =>
            line.canSell === true &&
            line.departamentoCodigo !== this.departmentCode &&
            line.stock > 0
        );

        console.log({
          validProductLines: this.validProductLines,
          invalidProductLines: this.invalidProductLines,
          transferableProductLines: this.transferableProductLines
        });
        // Select cheapest valid warehouse where line is not "FBA" and not "OB" (openbox)
        const selectableLines = this.validProductLines.filter(
          line => line.warehouse !== "FBA" && line.line !== "OB"
        );
        console.log({ selectableLines });

        if (selectableLines.length > 0) {
          const priceKey = this.transformedPriceLevel;
          const best = selectBestLine(selectableLines, priceKey);

          this.newProduct.price = best[this.transformedPriceLevel];
          this.newProduct.productId = best.productId;
          this.newProduct.coreNkrCode = best.coreNkrCode || null;
          this.newProduct.line = best.line;
          this.newProduct.warehouseId = best.warehouseId;
          this.newProduct.warehouseCode = best.warehouse;
          this.newProduct.departmentCode = best.departamentoCodigo;
        } else {
          // No valid warehouses - clear selection fields
          this.newProduct.price = null;
          this.newProduct.productId = null;
          this.newProduct.line = null;
          this.newProduct.warehouseId = null;
          this.newProduct.warehouseCode = null;
          this.newProduct.departmentCode = null;
        }
      }

      this.$emit("item-selected");
    },
    selectCustomItem() {
      this.newProduct.price = "";
      this.newProduct.productId = this.newProduct.nkrCode;
      this.newProduct.brand = "";
      this.newProduct.quantity = "";
      this.newProduct.line = "";
      this.newProduct.description = "";
      this.newProduct.kitCode = "";
      this.newProduct.core = "";
      this.newProduct.coreNkrCode = null;
      this.newProduct.custom = true;
      this.newProduct.departmentCode = this.departmentCode;
      this.options = null;
    },
    selectProductLine(productLine) {
      // Only allow selection if canSell is true AND same department
      if (
        productLine.canSell !== true ||
        productLine.departamentoCodigo !== this.departmentCode
      ) {
        this.$toast.open({
          message: "This warehouse cannot sell this product",
          type: "error",
          position: "top-right"
        });
        return;
      }

      this.newProduct.price = productLine[this.transformedPriceLevel];
      this.newProduct.productId = productLine.productId;
      this.newProduct.coreNkrCode = productLine.coreNkrCode || null;
      this.newProduct.line = productLine.line;
      this.newProduct.warehouseId = productLine.warehouseId;
      this.newProduct.warehouseCode = productLine.warehouse;
      this.newProduct.departmentCode = productLine.departamentoCodigo;
      this.newProduct.custom = false;
      this.newProduct.isTransfer = false;
    },
    selectProductLineAsTransfer(productLine) {
      // Select a product from a different warehouse as a transfer
      // Uses the price from the source warehouse with stock
      this.newProduct.price = productLine[this.transformedPriceLevel];
      this.newProduct.productId = productLine.productId;
      this.newProduct.coreNkrCode = productLine.coreNkrCode || null;
      this.newProduct.line = "TRANSFER";
      this.newProduct.warehouseId = productLine.warehouseId;
      this.newProduct.warehouseCode = productLine.warehouse;
      this.newProduct.departmentCode = productLine.departamentoCodigo;
      this.newProduct.sourceDepartmentCode = productLine.departamentoCodigo;
      this.newProduct.targetDepartmentCode = this.departmentCode;
      this.newProduct.custom = false;
      this.newProduct.isTransfer = true;
    },
    isTablet() {
      const userAgent = navigator.userAgent.toLowerCase();
      const isTablet = /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(
        userAgent
      );
      console.log("userAgent", { userAgent }, isTablet);
      return isTablet;
    },
    locateOptionBox() {
      const inputElement = document.getElementById("code-input");
      const box = document.getElementById("option-box");

      // Exit early if elements don't exist (modal uses different structure)
      if (!inputElement || !box) {
        return;
      }

      const inputPosition = inputElement.getBoundingClientRect();
      console.log(inputPosition.top, window.innerHeight);
      const percentaje = (100 * inputPosition.top) / window.innerHeight;
      let positionDiff = inputPosition.height + 20;
      if (percentaje > 60) {
        positionDiff = -box.offsetHeight - 20;
      }

      if (this.isTablet()) {
        box.style.top = `0px`;
        box.style.width = `100%`;
        box.style.left = `0px`;
      } else {
        box.style.top = `${inputPosition.top + positionDiff}px`;
      }
      console.log(positionDiff);
    },
    searchPart(code) {
      this.newProductLines = [];
      this.selected = false;
      code = code ? code.replaceAll(" ", "") : "";
      this.currentApiCallId++;
      let apiId = this.currentApiCallId;
      const search = debounce(async code => {
        if (code && code.length > 3) {
          getProducts(this.apiSearchFilters(code, 1)).then(res => {
            if (apiId !== this.currentApiCallId) {
              console.warn("another call is in process");
              return;
            }
            this.options = res.products;
            this.apiPage = 1;
            this.apiTotalPages = res.totalPages || 0;
            this.$nextTick(() => {
              this.locateOptionBox();
            });
          });
        }
      }, 600);
      search(code);
    },
    apiSearchFilters(code, page) {
      return {
        storeEnabled: "enabled",
        page,
        limitPerPage: "30",
        orderCol: "nkr_code",
        orderDir: "asc",
        search: code,
        requestPrice: true,
        priceLevel: "online",
        departmentCode: this.departmentCode
      };
    },
    async loadMoreApi() {
      if (!this.apiHasMore) return;
      const apiId = this.currentApiCallId;
      const code = this.modalSearchQuery
        ? this.modalSearchQuery.replaceAll(" ", "")
        : "";
      this.apiLoadingMore = true;
      try {
        const res = await getProducts(
          this.apiSearchFilters(code, this.apiPage + 1)
        );
        // Query changed while loading: discard
        if (apiId !== this.currentApiCallId) return;
        this.options = this.options.concat(res.products);
        this.apiPage++;
        this.apiTotalPages = res.totalPages || 0;
      } catch (e) {
        console.error("Failed to load more products", e);
      } finally {
        this.apiLoadingMore = false;
      }
    },
    saveChanges() {
      if (this.valid) {
        this.newProduct.pricesAndStock = this.newProductLines;
        console.log("newProduct", this.newProduct);
        this.$emit("addProduct", this.newProduct);
        this.clearForm();
        this.newProductLines = [];
        this.kitInformation = null;
      }
    },
    addCustomProduct() {
      if (
        this.newProduct.nkrCode != "" &&
        this.newProduct.price !== 0 &&
        this.newProduct.stock !== 0
      ) {
        this.newProduct.productId = `custom-${this.newProduct.nkrCode}`;
        this.$emit("addProduct", this.newProduct);
        this.newProduct = {
          nkrCode: "",
          description: "",
          brand: "",
          price: 0,
          quantity: 1,
          kitCode: ""
        };
        this.newProductLines = [];
        this.kitInformation = null;
      }
    },
    addKit() {
      const newProduct = {
        kitCode: this.kitInformation.code,
        kitPrice: this.kitInformation.price,
        kitQuantity: this.newProduct.quantity,
        kitImage: this.newProduct.image,
        kitStock: this.kitInformation.stock,
        kitBrand: this.kitInformation.brand,
        kitTitle: this.newProduct.description,
        kitItems: this.kitInformation.items.map(item => {
          return {
            nkrCode: item.nkrCode,
            image: item.imageRoute,
            description: item.name,
            brand: item.brand,
            price: item.price,
            quantity: item.quantity,
            line: item.line,
            productId: item.productId
          };
        }),
        quantity: this.newProduct.quantity
      };
      this.$emit("addKit", newProduct);
      this.kitInformation = null;
      this.newProduct = {
        nkrCode: "",
        description: "",
        brand: "",
        price: 0,
        quantity: 1,
        kitCode: ""
      };
    }
  },
  components: { NewProductLineKitData, StockSummaryTags, SearchResultItem }
};
</script>
<style lang="postcss" scoped>
/* Add Product Trigger Button */
.add-product-trigger {
  padding: 1.5rem;
  background: #f9fafb;
  border-radius: 0.5rem;
}

.add-product-compact {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  min-height: 44px;
  padding: 0 0.875rem;
  border: 1px dashed #9ca3af;
  border-radius: 10px;
  background: #fafafa;
  color: #126e6a;
  text-align: left;
  transition: background 0.15s, border-color 0.15s;
}

.add-product-compact:hover {
  border-color: #126e6a;
  background: rgba(18, 110, 106, 0.06);
}

.add-product-compact i {
  font-size: 1.25rem;
}

.add-product-compact .add-label {
  font-size: 0.9375rem;
  font-weight: 600;
  white-space: nowrap;
}

.add-product-compact .add-hint {
  min-width: 0;
  overflow: hidden;
  font-size: 0.875rem;
  color: #6b7280;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.add-product-compact .add-search {
  margin-left: auto;
  color: #6b7280;
}

/* Search Results Grid */
.load-more-wrap {
  display: flex;
  justify-content: center;
  padding: 16px 0 8px;
}
.load-more-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 200px;
  min-height: 48px;
  padding: 10px 24px;
  font-size: 1.05rem;
  font-weight: 600;
  color: #1d4ed8;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  transition: background 0.15s;
}
.load-more-btn:hover:not(:disabled) {
  background: #dbeafe;
}
.load-more-btn:disabled {
  opacity: 0.7;
  cursor: default;
}
.search-results-grid {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.search-result-item {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  overflow: hidden;
  transition: box-shadow 0.15s ease;
}

.search-result-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.result-image {
  width: 70px;
  height: 70px;
  flex-shrink: 0;
  background: #f9fafb;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Selected Product Card */
.selected-product-card {
  border-radius: 0.75rem;
  padding: 1rem;

  &.normal {
    background: #eff6ff;
    border: 1px solid #bfdbfe;
  }

  &.custom {
    background: #fff7ed;
    border: 1px solid #fed7aa;
  }
}

.selected-product-image {
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  background: white;
  border-radius: 0.5rem;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Warehouse Selection */
.warehouse-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.warehouse-option {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: white;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #a7f3d0;
    background: #f0fdf4;
  }

  &.selected {
    border-color: #22c55e;
    background: #f0fdf4;
  }

  &.transfer-option {
    border-color: #bfdbfe;
    background: #eff6ff;

    &:hover {
      border-color: #93c5fd;
      background: #dbeafe;
    }

    &.selected {
      border-color: #3b82f6;
      background: #dbeafe;
    }
  }
}

/* Form Fields */
.edit-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-top: 1rem;
}

.form-field {
  &.full-width {
    grid-column: span 2;
  }

  label {
    display: block;
    font-size: 0.9375rem;
    color: #6b7280;
    margin-bottom: 0.375rem;
    font-weight: 500;
  }

  input {
    width: 100%;
    padding: 0.875rem 1rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    font-size: 1.1rem;
    transition: border-color 0.15s ease;

    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
    }

    &:disabled {
      background: #f3f4f6;
      color: #9ca3af;
    }
  }
}

/* Action Buttons */
.action-btn {
  flex: 1;
  padding: 1rem 1.25rem;
  border-radius: 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  text-align: center;
  transition: all 0.15s ease;

  &.primary {
    background: #16a34a;
    color: white;

    &:hover {
      background: #15803d;
    }
  }

  &.custom {
    background: #f97316;
    color: white;

    &:hover {
      background: #ea580c;
    }
  }

  &.kit {
    background: #2563eb;
    color: white;

    &:hover {
      background: #1d4ed8;
    }
  }

  &.transfer {
    background: #3b82f6;
    color: white;

    &:hover {
      background: #2563eb;
    }
  }

  &.warning {
    background: #fef08a;
    color: #713f12;
  }

  &.disabled {
    background: #e5e7eb;
    color: #9ca3af;
  }
}

.cancel-btn {
  background: #e5e7eb;
  color: #374151;
  padding: 1rem 1.5rem;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 500;
  transition: background 0.15s ease;

  &:hover {
    background: #d1d5db;
  }
}
</style>

<!-- Global styles for modal (not scoped so they work properly) -->
<style lang="postcss">
/* Search Modal Overlay - Responsive */
.search-modal-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  z-index: 2147483647 !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  -webkit-overflow-scrolling: touch;
  isolation: isolate;

  /* Mobile: Full screen, no backdrop */
  background-color: white !important;

  /* Desktop: Semi-transparent backdrop */
  @media (min-width: 768px) {
    background-color: rgba(0, 0, 0, 0.5) !important;
  }

  /* Hidden state when product modal is open */
  &.modal-hidden {
    visibility: hidden;
    pointer-events: none;
  }
}

/* Search Modal Content - Responsive */
.search-modal-content {
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
  background-color: white !important;

  /* Mobile: Full screen */
  width: 100vw !important;
  height: 100vh !important;
  height: 100dvh !important;
  border-radius: 0;

  /* Tablet: Larger centered panel */
  @media (min-width: 768px) {
    width: 90vw !important;
    max-width: 600px !important;
    height: 85vh !important;
    max-height: 700px !important;
    border-radius: 1rem;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  }

  /* Desktop: Even larger panel */
  @media (min-width: 1024px) {
    max-width: 700px !important;
    height: 80vh !important;
    max-height: 800px !important;
  }
}

/* Search Mode Toggle */
.search-mode-toggle {
  display: flex;
  gap: 0;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  overflow: hidden;
}

.mode-btn {
  flex: 1;
  padding: 0.375rem 0.75rem;
  font-size: 0.8125rem;
  font-weight: 600;
  background: #f9fafb;
  color: #6b7280;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.mode-btn + .mode-btn {
  border-left: 1px solid #d1d5db;
}

.mode-btn.active {
  background: #111827;
  color: white;
}

.mode-btn.typesense.active {
  background: #5468ff;
  color: white;
}

.in-stock-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
  user-select: none;

  input[type="checkbox"] {
    width: 1rem;
    height: 1rem;
    cursor: pointer;
  }
}

/* Modal Header */
.modal-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;

  @media (min-width: 768px) {
    padding: 1.25rem;
  }
}

/* Modal Footer */
.modal-footer {
  display: flex;
  gap: 1rem;
  padding: 1.25rem;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;

  @media (min-width: 768px) {
    padding: 1.5rem 2rem;
  }
}

/* Ensure it works on iOS Safari */
@supports (-webkit-touch-callout: none) {
  .search-modal-content {
    height: -webkit-fill-available;
  }
}

/* Override parent overflow when modal is open */
body.modal-open .container-custom {
  overflow: visible !important;
}
</style>
